/**
 * Cosmo Crypt - Duress Mode & Panic Kill-Switch Service (Point 3)
 * 
 * Provides:
 * - Plausible Deniability (Duress PIN triggers decoy mode using the app's default mock cards)
 * - Suppression of real cloud vault fetching during Duress mode
 * - Emergency Panic Kill-Switch (Zeroize memory, wipe local storage/cache, terminate Firebase session)
 */

const DURESS_FLAG_KEY = '__cc_duress_active';

/**
 * Check if the active session is currently running in Duress / Decoy mode
 */
export function isDuressActive() {
    try {
        if (typeof window !== 'undefined') {
            if (window.__cc_is_duress === true) return true;
            return sessionStorage.getItem(DURESS_FLAG_KEY) === 'true';
        }
    } catch (e) { }
    return false;
}

/**
 * Activate or deactivate Duress Mode for the current session
 */
export function setDuressMode(isActive = true) {
    try {
        if (typeof window !== 'undefined') {
            window.__cc_is_duress = !!isActive;
            if (isActive) {
                sessionStorage.setItem(DURESS_FLAG_KEY, 'true');
            } else {
                sessionStorage.removeItem(DURESS_FLAG_KEY);
            }

            // Notify all active view layers and iframes
            window.dispatchEvent(new CustomEvent('cosmo:duress-changed', { detail: { isDuress: !!isActive } }));
            try {
                document.querySelectorAll('iframe').forEach(frame => {
                    try {
                        frame.contentWindow?.postMessage?.({ type: 'cosmo-duress-state', isDuress: !!isActive }, '*');
                    } catch (err) { }
                });
            } catch (e) { }
        }
    } catch (e) {
        console.warn("Could not set duress mode:", e);
    }
}

/**
 * Emergency Panic Wipe (Self-Destruct)
 * 
 * Instantly:
 * 1. Zeroes out all active session keys & decryption tokens from memory.
 * 2. Clears all LocalStorage & SessionStorage cache on the device.
 * 3. Purges browser IndexedDB databases.
 * 4. Clears active clipboard text.
 * 5. Dispatches silent sign-out to Firebase Auth.
 * 6. Cleanly redirects the user to the unauthenticated landing view.
 */
/**
 * Emergency Nuclear Panic Wipe (Global Session Kill & Total Local Browser Wipe)
 * 
 * Instantly:
 * 1. Dispatches Cloud Function to revoke active Firebase refresh tokens across ALL devices.
 * 2. Zeroes out all active session keys & decryption tokens from RAM memory.
 * 3. Clears all LocalStorage & SessionStorage caches on the device.
 * 4. Purges all browser IndexedDB databases for the origin.
 * 5. Deletes all CacheStorage & PWA service worker caches.
 * 6. Unregisters all active Service Workers.
 * 7. Overwrites and expires all origin cookies.
 * 8. Clears active clipboard text.
 * 9. Dispatches client sign-out to Firebase Auth.
 * 10. Sanitizes navigation history and redirects cleanly to the landing view.
 */
export async function executePanicWipe(skipCloudCall = false) {
    const rootWin = (typeof window !== 'undefined' ? (window.top || window.parent || window) : null) || window;

    // 0. Instantly suppress, hide, and clean up any lock screens, pause overlays, or active iframes on the root window
    try {
        if (rootWin.SessionProtocolParent && typeof rootWin.SessionProtocolParent.hideOverlay === 'function') {
            rootWin.SessionProtocolParent.hideOverlay();
        }
        if (window.SessionProtocolParent && typeof window.SessionProtocolParent.hideOverlay === 'function') {
            window.SessionProtocolParent.hideOverlay();
        }
        if (rootWin._0x83e2) {
            if (typeof rootWin._0x83e2._0xR3z === 'function') rootWin._0x83e2._0xR3z();
            if (typeof rootWin._0x83e2.setLockState === 'function') rootWin._0x83e2.setLockState(false);
            rootWin._0x83e2._0x2k8f = false;
        }
        if (window._0x83e2) {
            if (typeof window._0x83e2._0xR3z === 'function') window._0x83e2._0xR3z();
            if (typeof window._0x83e2.setLockState === 'function') window._0x83e2.setLockState(false);
            window._0x83e2._0x2k8f = false;
        }
        
        // Remove pause and lock overlays from DOM immediately
        const docList = [document];
        if (rootWin.document && rootWin.document !== document) docList.push(rootWin.document);
        docList.forEach(d => {
            try {
                const pauseEl = d.getElementById('session-pause-overlay');
                if (pauseEl) {
                    pauseEl.style.display = 'none';
                    pauseEl.remove();
                }
                const lockEl = d.getElementById('app-lock-screen');
                if (lockEl) {
                    lockEl.style.display = 'none';
                    lockEl.remove();
                }
            } catch(e) {}
        });
    } catch (e) { }

    // 1. High-speed Multi-Channel Firestore Broadcast (Guaranteed real-time delivery to all active remote devices)
    if (!skipCloudCall) {
        try {
            const authInstance = (rootWin && rootWin.suiteAuth) || (rootWin && rootWin.auth) || (window.parent && window.parent.suiteAuth) || window.suiteAuth || window.auth || (typeof firebase !== 'undefined' && firebase.auth ? firebase.auth() : null);
            const userStore = (rootWin && rootWin.userData) || (window.parent && window.parent.userData) || window.userData;
            const currentUser = (authInstance && authInstance.currentUser) || userStore;
            
            let uid = (currentUser && currentUser.uid) || (authInstance && authInstance.currentUser && authInstance.currentUser.uid) || localStorage.getItem('last_user_uid') || localStorage.getItem('last_active_uid');
            if (!uid) {
                try {
                    const enc = sessionStorage.getItem('userStoreEnc') || localStorage.getItem('userStoreEnc');
                    if (enc && typeof CryptoJS !== 'undefined') {
                        const secret = typeof _getAppSecret === 'function' ? _getAppSecret() : (rootWin && typeof rootWin._getAppSecret === 'function' ? rootWin._getAppSecret() : null);
                        if (secret) {
                            const bytes = CryptoJS.AES.decrypt(enc, secret);
                            const parsed = JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
                            if (parsed && parsed.uid) uid = parsed.uid;
                        }
                    }
                } catch (e) { }
            }

            const dbInstance = (rootWin && rootWin.suiteDb) || (window.parent && window.parent.suiteDb) || window.suiteDb || (typeof firebase !== 'undefined' && firebase.firestore ? firebase.firestore() : null);

            if (uid && dbInstance) {
                const now = Date.now();
                const userPayload = {
                    panicWipeAt: now,
                    panicEpoch: now,
                    lastLoginDevice: "Revoked (Panic Mode)",
                    activeSessions: [],
                    forceLogoutAll: true
                };
                const lockoutPayload = {
                    panicWipeAt: now,
                    panicEpoch: now,
                    forceLogoutAll: true,
                    reason: 'PANIC_WIPE'
                };

                const writePromises = [];
                const suiteSetDoc = (rootWin && rootWin.suiteSetDoc) || (window.parent && window.parent.suiteSetDoc) || window.suiteSetDoc;
                const suiteDoc = (rootWin && rootWin.suiteDoc) || (window.parent && window.parent.suiteDoc) || window.suiteDoc;

                if (typeof suiteSetDoc === 'function' && typeof suiteDoc === 'function') {
                    writePromises.push(suiteSetDoc(suiteDoc(dbInstance, "users", uid), userPayload, { merge: true }).catch((err) => console.warn("Panic user write:", err)));
                    writePromises.push(suiteSetDoc(suiteDoc(dbInstance, "users", uid, "security", "lockout_status"), lockoutPayload, { merge: true }).catch((err) => console.warn("Panic lockout_status write:", err)));
                    writePromises.push(suiteSetDoc(suiteDoc(dbInstance, "security_lockouts", uid), lockoutPayload, { merge: true }).catch((err) => console.warn("Panic security_lockouts write:", err)));
                } else if (dbInstance.collection) {
                    writePromises.push(dbInstance.collection("users").doc(uid).set(userPayload, { merge: true }).catch(() => {}));
                    writePromises.push(dbInstance.collection("users").doc(uid).collection("security").doc("lockout_status").set(lockoutPayload, { merge: true }).catch(() => {}));
                    writePromises.push(dbInstance.collection("security_lockouts").doc(uid).set(lockoutPayload, { merge: true }).catch(() => {}));
                }

                // Call backend Cloud Function to revoke all refresh tokens in Firebase Admin SDK
                const functionsInstance = (rootWin && rootWin.suiteFunctions) || (window.parent && window.parent.suiteFunctions) || window.suiteFunctions || null;
                const callableMaker = (rootWin && rootWin.suiteHttpsCallable) || (window.parent && window.parent.suiteHttpsCallable) || window.suiteHttpsCallable || null;
                if (functionsInstance && typeof callableMaker === 'function') {
                    try {
                        const revokeFn = callableMaker(functionsInstance, 'revokeUserTokensOnPanic');
                        writePromises.push(revokeFn({ uid: uid }).catch((err) => console.warn("Cloud token revocation error:", err)));
                    } catch (fnErr) { }
                }

                // Explicitly wait up to 1500ms to guarantee network broadcast reaches Firebase servers
                await Promise.race([
                    Promise.allSettled(writePromises),
                    new Promise(res => setTimeout(res, 1500))
                ]);
            }
        } catch (signalErr) { }
    }

    // 2. Broadcast across all local open browser tabs immediately
    try {
        if (typeof BroadcastChannel !== 'undefined') {
            const bc = new BroadcastChannel('cosmo_security_channel');
            bc.postMessage({ type: 'PANIC_WIPE', timestamp: Date.now() });
            bc.close();
        }
    } catch (e) { }

    // 3. Zeroize in-memory runtime credentials immediately
    try {
        window.userData = null;
        window.vaults = null;
        if (rootWin && rootWin !== window) {
            rootWin.userData = null;
            rootWin.vaults = null;
        }
    } catch (e) { }

    // 5. Clear clipboard safely without triggering browser permission prompts
    try {
        const canWriteDirect = typeof navigator !== 'undefined' && navigator.clipboard && navigator.clipboard.writeText && (!navigator.userActivation || navigator.userActivation.isActive);
        if (canWriteDirect) {
            await navigator.clipboard.writeText('').catch(() => {});
        } else if (typeof document !== 'undefined' && document.body) {
            const ta = document.createElement('textarea');
            ta.value = ' ';
            ta.style.position = 'fixed';
            ta.style.left = '-9999px';
            ta.style.top = '-9999px';
            ta.setAttribute('readonly', '');
            document.body.appendChild(ta);
            ta.select();
            try { document.execCommand('copy'); } catch (err) { }
            document.body.removeChild(ta);
        }
    } catch (e) { }

    // 6. Wipe IndexedDB databases completely
    try {
        if (window.indexedDB) {
            if (indexedDB.databases) {
                const dbs = await indexedDB.databases();
                for (const dbInfo of dbs) {
                    if (dbInfo.name) {
                        indexedDB.deleteDatabase(dbInfo.name);
                    }
                }
            }
            ['cosmocryptDB', 'firebaseLocalStorageDb', 'firestore/[DEFAULT]/[DEFAULT]/main', 'keyval-store'].forEach(name => {
                try { indexedDB.deleteDatabase(name); } catch(err){}
            });
        }
    } catch (e) { }

    // 7. Wipe CacheStorage (PWA & Offline Assets)
    try {
        if (typeof window !== 'undefined' && 'caches' in window && caches.keys) {
            const cacheKeys = await caches.keys();
            for (const name of cacheKeys) {
                await caches.delete(name);
            }
        }
    } catch (e) { }

    // 8. Unregister Service Workers
    try {
        if (typeof navigator !== 'undefined' && navigator.serviceWorker && navigator.serviceWorker.getRegistrations) {
            const registrations = await navigator.serviceWorker.getRegistrations();
            for (const reg of registrations) {
                await reg.unregister();
            }
        }
    } catch (e) { }

    // 9. Expire & Erase Cookies across all paths and domains
    try {
        if (typeof document !== 'undefined' && document.cookie) {
            const cookies = document.cookie.split(";");
            for (const cookie of cookies) {
                const eqPos = cookie.indexOf("=");
                const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
                document.cookie = name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/";
                document.cookie = name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=" + window.location.hostname;
                document.cookie = name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=." + window.location.hostname;
            }
        }
    } catch (e) { }

    // 10. Clear Web Storage (localStorage & sessionStorage)
    try {
        sessionStorage.clear();
        localStorage.clear();
        if (rootWin.sessionStorage) rootWin.sessionStorage.clear();
        if (rootWin.localStorage) rootWin.localStorage.clear();
    } catch (e) { }

    // 11. Sign out of Firebase Auth locally
    try {
        const authInstance = (rootWin && rootWin.suiteAuth) || (rootWin && rootWin.auth) || (window.parent && window.parent.suiteAuth) || window.suiteAuth || window.auth || (typeof firebase !== 'undefined' && firebase.auth ? firebase.auth() : null);
        if (authInstance && typeof authInstance.signOut === 'function') {
            await authInstance.signOut();
        }
    } catch (e) { }

    // 12. Sanitized History, Iframe teardown & Clean Instant Redirect of Root Window
    try {
        const docList = [document];
        if (rootWin.document && rootWin.document !== document) docList.push(rootWin.document);
        docList.forEach(d => {
            try {
                const iframes = d.querySelectorAll('iframe');
                iframes.forEach(f => {
                    try { f.srcdoc = ''; f.src = 'about:blank'; } catch (e) { }
                });
            } catch (e) { }
        });
        if (rootWin.history && rootWin.history.replaceState) {
            rootWin.history.replaceState(null, '', '/');
        }
    } catch (e) { }

    setTimeout(() => {
        try {
            rootWin.location.replace('/');
        } catch (e) {
            window.location.replace('/');
        }
    }, 50);
}

// Global cross-tab panic wipe listener
if (typeof BroadcastChannel !== 'undefined') {
    try {
        const securityChannel = new BroadcastChannel('cosmo_security_channel');
        securityChannel.onmessage = (event) => {
            if (event.data && event.data.type === 'PANIC_WIPE') {
                executePanicWipe(true);
            }
        };
    } catch (e) { }
}

// Global attachment for Cosmo Crypt ecosystem & templates
const CosmoDuressService = {
    isDuressActive,
    setDuressMode,
    executePanicWipe
};

if (typeof window !== 'undefined') {
    window.CosmoDuress = CosmoDuressService;
    window.triggerPanicWipe = executePanicWipe;
    window.executePanicWipe = executePanicWipe;
    if (window.parent && window.parent !== window) {
        window.parent.CosmoDuress = CosmoDuressService;
        window.parent.triggerPanicWipe = executePanicWipe;
        window.parent.executePanicWipe = executePanicWipe;
    }
}

export default CosmoDuressService;
